import { Component, computed, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Tooltip } from 'primeng/tooltip';
import { CompanyService } from '@/app/services/company.service';
import { CountryService } from '@/app/services/country.service';
import { relativeTime } from '@/app/utils/relative-time';

const RECENT_LIMIT = 5;

@Component({
    standalone: true,
    selector: 'app-recent-companies-widget',
    imports: [RouterLink, Tooltip],
    template: `
        <div class="card flex flex-col gap-2">
            <div class="flex items-center justify-between mb-2">
                <span class="font-semibold text-lg">Recently added companies</span>
            </div>
            @if (recent().length === 0) {
                @if (companyService.loading()) {
                    <span class="text-sm text-muted-color">Loading…</span>
                } @else {
                    <span class="text-sm text-muted-color">No companies yet.</span>
                }
            } @else {
                <ul class="flex flex-col divide-y divide-surface-200 dark:divide-surface-700 m-0 p-0 list-none">
                    @for (c of recent(); track c.Id) {
                        <li>
                            <button
                                type="button"
                                class="flex items-center justify-between gap-3 py-3 w-full text-left hover:bg-surface-50 dark:hover:bg-surface-800 -mx-3 px-3 rounded"
                                (click)="goToCompanies()"
                            >
                                <div class="flex items-center gap-3 min-w-0">
                                    <i class="pi pi-building text-muted-color"></i>
                                    <div class="flex flex-col min-w-0">
                                        <span class="font-medium truncate">{{ c.Name }}</span>
                                        <span class="text-xs text-muted-color flex items-center gap-1.5">
                                            <span [class]="'flag flag-' + c.CountryCode.toLowerCase()" style="width: 16px; height: 11px"></span>
                                            <span>{{ countryService.getName(c.CountryCode) }}</span>
                                        </span>
                                    </div>
                                </div>
                                <span
                                    class="text-xs text-muted-color whitespace-nowrap"
                                    [pTooltip]="absoluteDate(c.CreatedAt)"
                                    tooltipPosition="left"
                                >{{ relative(c.CreatedAt) }}</span>
                            </button>
                        </li>
                    }
                </ul>
            }
            <a routerLink="/companies" class="text-primary text-sm mt-2 self-start">View all →</a>
        </div>
    `,
})
export class RecentCompaniesWidget {
    protected readonly companyService = inject(CompanyService);
    protected readonly countryService = inject(CountryService);
    private readonly router = inject(Router);

    protected readonly recent = computed(() =>
        [...this.companyService.companies()]
            .filter(c => c.DeletedAt === null)
            .sort((a, b) => b.CreatedAt.localeCompare(a.CreatedAt))
            .slice(0, RECENT_LIMIT)
    );

    protected relative(when: string): string {
        return relativeTime(when);
    }

    protected absoluteDate(when: string): string {
        return new Date(when).toLocaleString();
    }

    protected goToCompanies(): void {
        this.router.navigate(['/companies']);
    }
}
